import { Bookmark, Download, History, MoreHorizontal, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { create } from "zustand";
import { formatAccelerator } from "@ide/shared";
import { desktop, isMac } from "@/desktop/bridge";
import { toast } from "sonner";
import type { AiProvider } from "@/desktop/ui";
import { cn, timeAgo } from "@/lib/utils";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tip,
} from "./ui/overlay";

/**
 * Histórico dos chats de IA (Copilot e Claude), como o "Mostrar chats" do VS Code: cada conversa
 * fica guardada neste computador com título, data, modelo e favoritos — a conversa inteira e as
 * respostas marcadas. Nova sessão começa do zero; abrir uma do histórico continua de onde parou.
 */
export interface ChatSession<M = unknown> {
  id: string;
  provider: AiProvider;
  projectId: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  /** Modelo usado por último ("Auto · Sonnet 5.5", "GPT-5 mini"…). */
  model: string | null;
  bookmarked: boolean;
  /** Índices das mensagens marcadas como favoritas. */
  bookmarks: number[];
  messages: M[];
}

const MAX_SESSIONS = 40;
const MAX_CHARS = 1_500_000;
const key = (p: AiProvider) => `kompil.chat.history.${p}`;

function read(p: AiProvider): ChatSession[] {
  try {
    const raw = JSON.parse(localStorage.getItem(key(p)) ?? "[]") as unknown;
    return Array.isArray(raw) ? (raw as ChatSession[]).filter((s) => s && typeof s.id === "string") : [];
  } catch {
    return [];
  }
}

function write(p: AiProvider, sessions: ChatSession[]) {
  // As mais novas (e as favoritas) ficam; o resto sai quando passa do limite de espaço.
  let list = [...sessions].sort(
    (a, b) => Number(b.bookmarked) - Number(a.bookmarked) || b.updatedAt - a.updatedAt,
  );
  list = list.slice(0, MAX_SESSIONS);
  let json = JSON.stringify(list);
  while (json.length > MAX_CHARS && list.length > 1) {
    list = list.slice(0, -1);
    json = JSON.stringify(list);
  }
  try {
    localStorage.setItem(key(p), json);
  } catch {
    // armazenamento cheio ou bloqueado: o histórico vale só nesta sessão
  }
  return list;
}

interface HistoryState {
  sessions: Record<AiProvider, ChatSession[]>;
  save(session: ChatSession): void;
  remove(provider: AiProvider, id: string): void;
  toggleBookmark(provider: AiProvider, id: string): void;
  toggleMessage(provider: AiProvider, id: string, index: number): void;
  clear(provider: AiProvider): void;
}

export const useChatHistory = create<HistoryState>((set) => {
  const update = (p: AiProvider, fn: (list: ChatSession[]) => ChatSession[]) =>
    set((s) => ({ sessions: { ...s.sessions, [p]: write(p, fn(s.sessions[p])) } }));
  return {
    sessions: { claude: read("claude"), copilot: read("copilot") },
    save: (session) =>
      update(session.provider, (list) => {
        const prev = list.find((s) => s.id === session.id);
        const next = { ...session, bookmarked: prev?.bookmarked ?? session.bookmarked };
        return [next, ...list.filter((s) => s.id !== session.id)];
      }),
    remove: (p, id) => update(p, (list) => list.filter((s) => s.id !== id)),
    toggleBookmark: (p, id) =>
      update(p, (list) => list.map((s) => (s.id === id ? { ...s, bookmarked: !s.bookmarked } : s))),
    toggleMessage: (p, id, index) =>
      update(p, (list) =>
        list.map((s) =>
          s.id === id
            ? {
                ...s,
                bookmarks: s.bookmarks.includes(index)
                  ? s.bookmarks.filter((i) => i !== index)
                  : [...s.bookmarks, index],
              }
            : s,
        ),
      ),
    clear: (p) => update(p, (list) => list.filter((s) => s.bookmarked)),
  };
});

/**
 * Conversas do Copilot Chat do VS Code neste computador entram no histórico do Kompil (abrir uma
 * continua de onde parou, com a conversa como contexto). As que já vieram não duplicam.
 */
export async function importVsCodeCopilot(projectId: string) {
  const chats = await desktop?.copilot?.vscodeChats?.().catch(() => []);
  if (!chats?.length) {
    toast("Nenhuma conversa do Copilot encontrada no VS Code deste computador.");
    return;
  }
  const known = new Set(useChatHistory.getState().sessions.copilot.map((s) => s.id));
  let added = 0;
  for (const c of chats) {
    if (known.has(c.id)) continue;
    useChatHistory.getState().save({
      id: c.id,
      provider: "copilot",
      projectId,
      title: c.title || "Conversa do VS Code",
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      model: c.model,
      bookmarked: false,
      bookmarks: [],
      messages: c.messages.map((m) => ({
        role: m.role,
        parts: [{ type: "text", text: m.text }],
        ...(m.at ? { startedAt: m.at, doneAt: m.at } : {}),
        ...(m.role === "assistant" && c.model ? { model: c.model } : {}),
      })),
    });
    added++;
  }
  toast.success(
    added
      ? `${added} ${added === 1 ? "conversa do VS Code entrou" : "conversas do VS Code entraram"} no histórico.`
      : "As conversas do VS Code já estão no histórico.",
  );
}

/** A sessão (do histórico) com este id, ou nada. */
export const findSession = (p: AiProvider, id: string | null) =>
  id ? (useChatHistory.getState().sessions[p].find((s) => s.id === id) ?? null) : null;

/** Título da conversa: o começo do primeiro pedido. */
export function sessionTitle(firstPrompt: string): string {
  const line = firstPrompt.replace(/\s+/g, " ").trim();
  return line.length > 60 ? `${line.slice(0, 57)}…` : line || "Nova conversa";
}

function groupOf(at: number): string {
  const d = new Date(at);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Hoje";
  const yesterday = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === yesterday.toDateString()) return "Ontem";
  if (Date.now() - at < 7 * 86_400_000) return "Últimos 7 dias";
  return "Mais antigas";
}

/** Lista do histórico: busca, favoritos em cima e as outras agrupadas por data. */
function HistoryList({
  provider,
  projectId,
  currentId,
  onOpen,
  onClose,
}: {
  provider: AiProvider;
  projectId: string;
  currentId: string | null;
  onOpen(session: ChatSession, messageIndex?: number): void;
  onClose(): void;
}) {
  const all = useChatHistory((s) => s.sessions[provider]);
  const { remove, toggleBookmark } = useChatHistory.getState();
  const [q, setQ] = useState("");
  const [onlyProject, setOnlyProject] = useState(true);
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return all
      .filter((s) => !onlyProject || s.projectId === projectId)
      .filter((s) => !term || s.title.toLowerCase().includes(term))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }, [all, q, onlyProject, projectId]);
  const favorites = list.filter((s) => s.bookmarked || s.bookmarks.length);
  const groups = new Map<string, ChatSession[]>();
  for (const s of list) groups.set(groupOf(s.updatedAt), [...(groups.get(groupOf(s.updatedAt)) ?? []), s]);

  const row = (s: ChatSession) => (
    <li key={s.id} className="group flex items-center gap-1 rounded-md pr-1 hover:bg-bg-muted">
      <button
        type="button"
        onClick={() => {
          onOpen(s);
          onClose();
        }}
        className={cn(
          "min-w-0 flex-1 px-2 py-1.5 text-left",
          s.id === currentId ? "text-fg" : "text-fg-muted hover:text-fg",
        )}
      >
        <span className="block truncate text-[12.5px]">{s.title}</span>
        <span className="block truncate text-[11px] text-fg-subtle">
          {timeAgo(new Date(s.updatedAt).toISOString())}
          {s.model ? ` · ${s.model}` : ""} · {s.messages.length} mensagens
        </span>
      </button>
      <Tip label={s.bookmarked ? "Tirar dos favoritos" : "Adicionar aos favoritos"}>
        <button
          type="button"
          onClick={() => toggleBookmark(provider, s.id)}
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-md hover:bg-bg-emphasis",
            s.bookmarked ? "text-fg" : "text-fg-subtle opacity-0 group-hover:opacity-100",
          )}
          aria-label={s.bookmarked ? "Tirar dos favoritos" : "Adicionar aos favoritos"}
        >
          <Bookmark className={cn("h-3.5 w-3.5", s.bookmarked && "fill-current")} />
        </button>
      </Tip>
      <Tip label="Apagar do histórico">
        <button
          type="button"
          onClick={() => remove(provider, s.id)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-subtle opacity-0 hover:bg-bg-emphasis hover:text-fg group-hover:opacity-100"
          aria-label="Apagar do histórico"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </Tip>
    </li>
  );

  return (
    <div className="flex max-h-[60vh] flex-col">
      <div className="border-b border-border p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar conversas"
            className="h-8 w-full rounded-md border border-border bg-bg pl-7 pr-2 text-[12.5px] text-fg placeholder:text-fg-subtle focus:border-fg-subtle focus:outline-none"
            aria-label="Buscar conversas"
            autoFocus
          />
        </div>
        <label className="mt-1.5 flex cursor-pointer items-center gap-1.5 text-[11.5px] text-fg-subtle">
          <input type="checkbox" checked={onlyProject} onChange={(e) => setOnlyProject(e.target.checked)} />
          Só deste projeto
        </label>
      </div>
      <div className="min-h-0 overflow-y-auto p-1">
        {!list.length ? (
          <p className="px-3 py-6 text-center text-[12px] text-fg-subtle">
            {q ? "Nenhuma conversa com esse título." : "As conversas aparecem aqui."}
          </p>
        ) : null}
        {favorites.length ? (
          <section className="mb-1">
            <p className="label-mono px-2 pb-0.5 pt-1.5">Favoritos</p>
            <ul>
              {favorites.filter((s) => s.bookmarked).map(row)}
              {favorites
                .flatMap((s) => s.bookmarks.map((i) => ({ s, i })))
                .map(({ s, i }) => {
                  const m = s.messages[i] as
                    { content?: string; parts?: Array<{ text?: string }> } | undefined;
                  const text = (m?.content ?? m?.parts?.find((p) => p.text)?.text ?? "").trim();
                  if (!text) return null;
                  return (
                    <li key={`${s.id}:${i}`}>
                      <button
                        type="button"
                        onClick={() => {
                          onOpen(s, i);
                          onClose();
                        }}
                        className="flex w-full items-start gap-1.5 rounded-md px-2 py-1.5 text-left text-fg-muted hover:bg-bg-muted hover:text-fg"
                      >
                        <Bookmark className="mt-0.5 h-3 w-3 shrink-0 fill-current" />
                        <span className="min-w-0">
                          <span className="line-clamp-2 text-[12px]">{text.slice(0, 180)}</span>
                          <span className="block truncate text-[11px] text-fg-subtle">em {s.title}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
            </ul>
          </section>
        ) : null}
        {[...groups].map(([label, sessions]) => (
          <section key={label} className="mb-1">
            <p className="label-mono px-2 pb-0.5 pt-1.5">{label}</p>
            <ul>{sessions.map(row)}</ul>
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * Barra do chat de IA (como o topo do Chat do VS Code): o título da conversa com o favorito, e à
 * direita Nova sessão, Histórico e o menu "…" (com as ações do próprio chat).
 */
export function ChatSessionBar({
  provider,
  projectId,
  title,
  sessionId,
  canBookmark,
  onNew,
  onOpen,
  extra,
}: {
  provider: AiProvider;
  projectId: string;
  title: string | null;
  sessionId: string | null;
  canBookmark: boolean;
  onNew(): void;
  onOpen(session: ChatSession, messageIndex?: number): void;
  /** Itens a mais no menu "…". */
  extra?: React.ReactNode;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const bookmarked = useChatHistory((s) =>
    Boolean(sessionId && s.sessions[provider].find((x) => x.id === sessionId)?.bookmarked),
  );
  const count = useChatHistory((s) => s.sessions[provider].filter((x) => x.projectId === projectId).length);
  const icon =
    "flex h-7 w-7 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg data-[state=open]:bg-bg-emphasis data-[state=open]:text-fg";
  return (
    <div className="flex h-9 shrink-0 items-center gap-0.5 border-b border-panel-border pl-3 pr-1.5">
      <p className="min-w-0 flex-1 truncate text-[12px] font-medium text-fg-muted" title={title ?? undefined}>
        {title ?? "Nova conversa"}
      </p>
      {canBookmark && sessionId ? (
        <Tip label={bookmarked ? "Tirar dos favoritos" : "Favoritar esta conversa"}>
          <button
            type="button"
            className={cn(icon, bookmarked && "text-fg")}
            onClick={() => useChatHistory.getState().toggleBookmark(provider, sessionId)}
            aria-label="Favoritar a conversa"
            aria-pressed={bookmarked}
          >
            <Bookmark className={cn("h-3.5 w-3.5", bookmarked && "fill-current")} />
          </button>
        </Tip>
      ) : null}
      <Tip label={`Nova sessão (${formatAccelerator("CmdOrCtrl+L", isMac)})`}>
        <button type="button" className={icon} onClick={onNew} aria-label="Nova sessão">
          <Plus className="h-4 w-4" />
        </button>
      </Tip>
      <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
        <Tip label={`Histórico de conversas${count ? ` (${count})` : ""}`}>
          <PopoverTrigger asChild>
            <button type="button" className={icon} aria-label="Histórico de conversas">
              <History className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
        </Tip>
        <PopoverContent align="end" className="w-80 p-0">
          <HistoryList
            provider={provider}
            projectId={projectId}
            currentId={sessionId}
            onOpen={onOpen}
            onClose={() => setHistoryOpen(false)}
          />
        </PopoverContent>
      </Popover>
      {provider === "copilot" && typeof desktop?.copilot?.vscodeChats === "function" ? (
        <Tip label="Trazer as conversas do Copilot do VS Code">
          <button
            type="button"
            className={icon}
            onClick={() => void importVsCodeCopilot(projectId)}
            aria-label="Trazer conversas do VS Code"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        </Tip>
      ) : null}
      <Menu>
        <Tip label="Mais ações">
          <MenuTrigger asChild>
            <button type="button" className={icon} aria-label="Mais ações do chat">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </MenuTrigger>
        </Tip>
        <MenuContent align="end" className="w-64">
          {extra}
          <MenuItem
            icon={<Trash2 className="h-3.5 w-3.5" />}
            onSelect={() => useChatHistory.getState().clear(provider)}
          >
            Limpar o histórico (mantém os favoritos)
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  );
}

/** Botão de favoritar uma resposta (no rodapé dela). */
export function BookmarkMessageButton({
  provider,
  sessionId,
  index,
}: {
  provider: AiProvider;
  sessionId: string | null;
  index: number;
}) {
  const on = useChatHistory((s) =>
    Boolean(sessionId && s.sessions[provider].find((x) => x.id === sessionId)?.bookmarks.includes(index)),
  );
  if (!sessionId) return null;
  return (
    <Tip label={on ? "Tirar dos favoritos" : "Favoritar esta resposta"}>
      <button
        type="button"
        onClick={() => useChatHistory.getState().toggleMessage(provider, sessionId, index)}
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-bg-muted hover:text-fg",
          on && "text-fg",
        )}
        aria-label={on ? "Tirar dos favoritos" : "Favoritar esta resposta"}
        aria-pressed={on}
      >
        <Bookmark className={cn("h-3.5 w-3.5", on && "fill-current")} />
      </button>
    </Tip>
  );
}

/** Rola até a mensagem (aberta pelo favorito) e a destaca por um instante. */
export function revealMessage(container: HTMLElement | null, index: number) {
  requestAnimationFrame(() => {
    const el = container?.querySelector<HTMLElement>(`[data-message="${index}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "center" });
    el.classList.add("ring-1", "ring-fg-subtle", "rounded-lg");
    setTimeout(() => el.classList.remove("ring-1", "ring-fg-subtle", "rounded-lg"), 1600);
  });
}
